import GPFieldServer from '@/components/GPFieldServer';
import { shouldShowFilterOptionsSearch } from '@/utils/filterOptionsSearch';
import { useGPFormContext } from '@/contexts/GPFormContext';
import { ArrayWithUID } from '@/requests/types';
import { CHECKBOX_GROUP_TYPE } from '@/types/forms/fields';
import { FormSectionType } from '@/types/forms';
import {
  alpha,
  Box,
  Divider,
  keyframes,
  Popover,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { get, set } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { GPButton } from '.';
import { GPButtonProps } from './GPButton';
import GPFilterRowMobile from './GPFilterRowMobile';
import GPFilterReverseButton from './GPFilterReverseButton';
import { isFilterFieldActive } from '@/utils/filterFieldActive';
import { isToggleFilterSection } from '@/utils/filterToggleSection';
import { useFilterToggleSection } from '@/hooks/useFilterToggleSection';

// Highlight animation for filter chips when updated via NLP
const filterHighlight = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.4);
  }
  50% {
    box-shadow: 0 0 0 4px rgba(25, 118, 210, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(25, 118, 210, 0);
  }
`;

const useStyles = makeStyles()(theme => ({
  popover: {
    zIndex: theme.zIndex.appBar - 1,
  },
  
  highlightAnimation: {
    animation: `${filterHighlight} 1.5s ease-out`,
  },

  fieldsContainer: {
    flexGrow: 1,
    marginBottom: theme.spacing(0),
    overflowY: 'auto',
    minHeight: 0,
    alignItems: 'center',
    padding: '0 1px',
    whiteSpace: 'nowrap',
  },

  /** Checkbox filters with option search — scroll lives inside GPCheckboxGroup */
  fieldsContainerFill: {
    flexGrow: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    padding: '0 1px',
  },

  filterActionButton: {
    marginRight: theme.spacing(3),
  },

  dateFiltersContainer: {
    maxWidth: 400,
  },

  switchContainer: {
    display: 'inline-flex',
    flexDirection: 'row-reverse',
    margin: 0,
    width: '100%',
    justifyContent: 'space-between',
  },

  switch: {
    marginLeft: theme.spacing(2),
  },

  inputField: {
    marginTop: '16px',
  },

  inputFieldFill: {
    marginTop: '16px',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
}));

/**
 * Single checkbox_group filters that show the option search scroll their list
 * internally (search stays fixed). Sections without search fall back to the
 * popover-level scroll container so their options are never clipped.
 */
function filterSectionUsesInternalOptionScroll(button: FormSectionType): boolean {
  return (
    button.fields.length === 1 &&
    button.fields[0].type === CHECKBOX_GROUP_TYPE &&
    shouldShowFilterOptionsSearch(button.fields[0].name)
  );
}

/** Shared chip styling so the popover chips and the toggle chips stay identical. */
const filterChipSx = (filterApplied: boolean) =>
  ({
    padding: theme => theme.spacing(1.25, 1.5),
    color: theme => (filterApplied ? theme.palette.primary.main : theme.palette.text.secondary),
    borderColor: filterApplied ? undefined : 'divider',
    boxShadow: filterApplied ? 0 : '0px 2px 11px 0px rgba(0,0,0,0.12)',
    lineHeight: 1.125,
    transition: 'box-shadow 0.5s ease-out',
    backgroundColor: theme =>
      filterApplied ? alpha(theme.palette.primary.main, 0.08) : theme.palette.background.paper,
  }) as const satisfies GPButtonProps['sx'];

/**
 * A filter with nothing to choose (Action Required) is a plain on/off — no popover,
 * no Apply. The chip is a toggle button and submits on click.
 */
const FilterRowToggleItem = ({ button }: { button: FormSectionType }) => {
  const { active, toggle } = useFilterToggleSection(button);

  return (
    <GPButton
      aria-pressed={active}
      onClick={toggle}
      variant="outlined"
      sx={filterChipSx(active)}
      size="small"
    >
      {button.label}
    </GPButton>
  );
};

type FilterRowItemProps = PropsWithChildren<{
  button: FormSectionType;
}>;

/**
  @deprecated New pages should not use dynamic components from BE
 */
const FilterRowItem = ({ button }: FilterRowItemProps) => {
  const [open, setOpen] = React.useState(false);
  const [localFiltered, setLocalFiltered] = React.useState(new Set<string>());
  const filterApplied = localFiltered.size > 0;
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const firstFieldRef = React.useRef<HTMLElement | null>(null);

  const { classes } = useStyles();

  const { t } = useTranslation();

  const id = `filter-${button.label_raw}`;

  const {
    getValues,
    defaultValues,
    reset,
    handleSubmit,
    onSubmit,
    valueState,
    setModified,
  } = useGPFormContext();

  const fieldNames = useMemo(() => button.fields.map(field => field.name), [button.fields]);

  // Closing without Apply is a cancel: revert this section's fields to the last applied
  // state (valueState) so pending, unsubmitted edits are discarded.
  const discardAndClose = () => {
    const newValues = { ...getValues() };
    fieldNames.forEach(name => {
      if (name) set(newValues, name, get(valueState, name));
    });
    reset(newValues);
    setOpen(false);
  };

  const handleToggle = () => {
    if (open) {
      discardAndClose();
    } else {
      setOpen(true);
    }
  };

  const usesInternalOptionScroll = useMemo(
    () => filterSectionUsesInternalOptionScroll(button),
    [button],
  );

  // This is super hacky. This is to focus the first input when the popover is opened.
  // Portal and popover help with accessibility, but in combo with dynamic fields focusing the first element after it's properly mounted is tough.
  // We need to move away from the GPFieldServer.
  const focusableSelectors =
    'a[href]:not([tabindex="-1"]):not([disabled]),' +
    'button:not([disabled]):not([tabindex="-1"]),' +
    'textarea:not([disabled]):not([tabindex="-1"]),' +
    'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]),' +
    'select:not([disabled]):not([tabindex="-1"]),' +
    '[tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const input = firstFieldRef.current?.querySelector<HTMLElement>(focusableSelectors);
          input?.focus();
        }, 0);
      });
    }
  }, [open, focusableSelectors]);

  // Track whether this chip's fields differ from defaults (active filter styling).
  useEffect(() => {
    const nextLocal = new Set<string>();

    fieldNames.forEach(name => {
      if (name && isFilterFieldActive(valueState, name, defaultValues)) {
        nextLocal.add(name);
      }
    });

    setLocalFiltered(nextLocal);

    setModified(prev => {
      const next = new Set(prev);
      fieldNames.forEach(name => {
        if (!name) return;
        if (nextLocal.has(name)) {
          next.add(name);
        } else {
          next.delete(name);
        }
      });
      return next;
    });
  }, [valueState, defaultValues, fieldNames, setModified]);

  const handleApply = () => {
    setOpen(false);
    handleSubmit(onSubmit)();
  };

  const handleReset = () => {
    const newValues = { ...getValues() };
    fieldNames.forEach(name => {
      name && set(newValues, name, get(defaultValues, name));
    });
    reset(newValues);
    setOpen(false);
    handleSubmit(onSubmit)();
    requestAnimationFrame(() => anchorRef.current?.focus());
  };

  const sxFilterBtn = filterChipSx(filterApplied);

  const handleClose = () => {
    discardAndClose();
  };

  return (
    <>
      <GPButton
        ref={anchorRef}
        aria-controls={open ? id : undefined}
        aria-haspopup="dialog"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleToggle}
        variant="outlined"
        sx={sxFilterBtn}
        size="small"
      >
        {button.label}
      </GPButton>
      <Popover
        className={classes.popover}
        id={id}
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        role="dialog"
        aria-modal="true"
        slotProps={{
          // sx (not a className) so these win over MUI v9's PopoverPaper defaults
          // (overflowY:'auto'), keeping the inner list the only scroller and the footer pinned
          paper: {
            sx: theme => ({
              display: 'flex',
              flexDirection: 'column',
              minWidth: 300,
              maxWidth: 600,
              maxHeight: 400,
              p: '8px 19px',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '10px',
              boxShadow: theme.shadows[1],
              overflow: 'hidden',
            }),
          },
        }}
      >
        <Box
          className={
            usesInternalOptionScroll ? classes.fieldsContainerFill : classes.fieldsContainer
          }
        >
          {button.fields.map((field, index) => (
            <Box
              ref={index === 0 ? firstFieldRef : null}
              className={usesInternalOptionScroll ? classes.inputFieldFill : classes.inputField}
              key={field.unique_id}
              sx={{
                mb: usesInternalOptionScroll ? 0 : 2
              }}
            >
              <GPFieldServer
                field={field}
                label={button.label}
                isFilter
                showSearch={shouldShowFilterOptionsSearch(field.name)}
              />
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
            mt: 1,
            mb: 1
          }}>
          <GPButton
            className={classes.filterActionButton}
            round
            color="primary"
            type="submit"
            variant="contained"
            onClick={handleApply}
          >
            {t('Apply')}
          </GPButton>
          <GPButton round color="primary" onClick={handleReset}>
            {t('Clear')}
          </GPButton>
          {button.reversible && (
            <Box sx={{ ml: 'auto' }}>
              <GPFilterReverseButton button={button} />
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

const ClearAllButton = () => {
  const { reset, defaultValues, handleSubmit, onSubmit, modified } = useGPFormContext();

  const { t } = useTranslation();

  const handleClearAll = () => {
    reset(defaultValues);
    handleSubmit(onSubmit)();
  };

  return modified.size > 0 ? (
    // Matches the filter chips' text colour rather than sitting a shade darker
    <Typography variant="caption" color="text.secondary">
      <GPButton link onClick={handleClearAll}>
        {t('Clear all')}
      </GPButton>
    </Typography>
  ) : null;
};

type GPFilterRowProps = {
  filterButtons: ArrayWithUID<FormSectionType>;
  /** When true, applies highlight animation to filter chips */
  highlight?: boolean;
};

const GPFilterRow = ({ filterButtons, highlight }: GPFilterRowProps) => {
  const smDown = useMediaQuery<Theme>(theme => theme.breakpoints.down('sm'));
  const { classes } = useStyles();

  return smDown ? (
    <GPFilterRowMobile filterButtons={filterButtons} highlight={highlight} />
  ) : (
    <Stack
      direction="row"
      className={highlight ? classes.highlightAnimation : undefined}
      sx={{
        gap: 1,
        alignItems: "center",
        flexWrap: "wrap"
      }}>
      {filterButtons.map(button => {
        if (button.hide) return null;

        return isToggleFilterSection(button) ? (
          <FilterRowToggleItem key={button.unique_id} button={button} />
        ) : (
          <FilterRowItem key={button.unique_id} button={button} />
        );
      })}
      <Divider />
      <ClearAllButton />
    </Stack>
  );
};

export default GPFilterRow;
